import { useEffect } from 'react';
import { Modal, Form, Input, Select, Row, Col, Alert, Button, useApp } from '../../ui';
import { FileUploadField } from '../../components/FileUploadField';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { confirmAction } from '../../lib/confirm';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { mobileRules } from '../../lib/validators';
import { PhoneInput } from '../../components/PhoneInput';
import { TerritoryPicker, type TerritorySelection } from '../../components/TerritoryPicker';
import { usePermission, useAuthStore } from '../../store/auth';
import { warehouseLabel, warehouseText } from '../../lib/warehouseLabel';

interface Props {
  open: boolean;
  onClose: () => void;
  record?: any;
}

// Staff roles only — RETAILER/FARMER logins are provisioned via the Retailer/Farmer forms (+ OTP), never created here.
// Keep in sync with STAFF_ROLES in the backend users.service.ts (the create/edit allow-list).
const ROLES = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNTS', 'WAREHOUSE_MANAGER', 'SALES_HEAD', 'SALES_PERSON'];

// Human-readable labels for the role dropdown — the raw UPPER_SNAKE codes are hard to read.
const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  ACCOUNTS: 'Accounts',
  WAREHOUSE_MANAGER: 'Warehouse Manager',
  SALES_HEAD: 'Sales Head',
  SALES_PERSON: 'Sales Person',
};

// Org hierarchy: who each role reports up to. SUPER_ADMIN is the top → no manager.
const REPORTS_TO: Record<string, { label: string; managerRoles: string[] }> = {
  SALES_PERSON: { label: 'Reports to (Sales Head)', managerRoles: ['SALES_HEAD'] },
  SALES_HEAD: { label: 'Reports to (Admin)', managerRoles: ['ADMIN'] },
  ACCOUNTS: { label: 'Reports to (Admin / Super Admin)', managerRoles: ['ADMIN', 'SUPER_ADMIN'] },
  ADMIN: { label: 'Reports to (Super Admin)', managerRoles: ['SUPER_ADMIN'] },
};

// Org seniority, most-senior first. A multi-role user's PRIMARY is their MOST SENIOR role — so
// a person who is both Sales Person and Sales Head is treated as a Sales Head (reports to Admin,
// not to a Sales Head). Roles not listed rank last. Keep in sync with the backend ROLE_SENIORITY.
const ROLE_SENIORITY = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNTS', 'SALES_HEAD', 'WAREHOUSE_MANAGER', 'SALES_PERSON'];
const sortBySeniority = (codes: string[]): string[] =>
  [...codes].sort((a, b) => {
    const ra = ROLE_SENIORITY.indexOf(a); const rb = ROLE_SENIORITY.indexOf(b);
    return (ra < 0 ? 99 : ra) - (rb < 0 ? 99 : rb);
  });
const LANGS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिंदी' },
  { value: 'mr', label: 'मराठी' },
  { value: 'bn', label: 'বাংলা' },
  { value: 'or', label: 'ଓଡ଼ିଆ' },
];

export function UserFormModal({ open, onClose, record }: Props) {
  const [form] = Form.useForm();
  const { message } = useApp();
  const qc = useQueryClient();
  const isEdit = !!record;
  // The login number is locked on edit — only a Super Admin may re-point it (it's the OTP identity).
  const isSuperAdmin = useAuthStore((s) => s.user?.role === 'SUPER_ADMIN' || (s.user?.roles ?? []).includes('SUPER_ADMIN'));
  // Multi-role: the form holds a SET of roles. The reporting line comes from the most senior role
  // that actually HAS a manager — so Sales Person + Sales Head reports to Admin (via the SH), and
  // Sales Person + Warehouse Manager still reports to a Sales Head (WHM has no manager of its own).
  // A Super Admin (the top) reports to no one.
  const roleCodes: string[] = Form.useWatch('roleCodes', form) ?? [];
  const reportsRole = roleCodes.includes('SUPER_ADMIN')
    ? undefined
    : ROLE_SENIORITY.find((r) => roleCodes.includes(r) && !!REPORTS_TO[r]);
  const reportsCfg = reportsRole ? REPORTS_TO[reportsRole] : undefined;

  // All staff — filtered client-side to the manager roles valid for the chosen role.
  const { data: allStaff } = useQuery({
    queryKey: ['staff-for-reports'],
    queryFn: () => requestAllPages('/users'),
    enabled: open,
  });
  const managerOptions = (allStaff?.data ?? [])
    .filter((u: any) => reportsCfg?.managerRoles.includes(u.role?.code ?? u.roleCode))
    .filter((u: any) => String(u.id) !== String(record?.id)) // can't report to yourself
    .map((u: any) => ({ value: String(u.id), label: u.fullName }));

  // Territory picker shows for a SP/SH (level follows the Sales role — SH wins); the warehouse
  // section shows for a WHM. A dual-hat Sales Head + Warehouse Manager sees BOTH.
  const terrRole = roleCodes.includes('SALES_HEAD') ? 'SALES_HEAD' : roleCodes.includes('SALES_PERSON') ? 'SALES_PERSON' : undefined;
  const isTerritoryRole = !!terrRole;
  const pickerIsSp = terrRole === 'SALES_PERSON';
  const isWarehouseManager = roleCodes.includes('WAREHOUSE_MANAGER');
  const reportsToVal = Form.useWatch('reportsTo', form);
  // The edited user's role (single-role policy) — drives the edit-time prefill queries.
  const recordRoleCodes: string[] = record ? [record.role?.code ?? record.roleCode].filter(Boolean) : [];
  const recordHasTerr = recordRoleCodes.includes('SALES_PERSON') || recordRoleCodes.includes('SALES_HEAD');

  // A Warehouse Manager can be bound to their warehouse(s) right here (gated by the same
  // permission as the Warehouse Managers page); mirrors that page's assign/unassign API.
  const canAssignWarehouse = usePermission('WAREHOUSE.MANAGER.ASSIGN');
  const { data: warehouses } = useQuery({
    queryKey: ['warehouses-for-user-form'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: open && isWarehouseManager && canAssignWarehouse,
  });
  const warehouseOptions = (warehouses ?? []).map((w: any) => ({
    value: String(w.id),
    // The trigger shows picks as small chips, so its text stays PLAIN (a chip inside a chip reads badly);
    // the dropdown row gets the full node: code chip, site name, gold MASTER capsule.
    text: warehouseText(w),
    label: warehouseLabel(w),
  }));
  const { data: existingAssignments } = useQuery({
    queryKey: ['user-wh-assignments', record?.id],
    queryFn: () => request<any[]>({ url: '/warehouse-assignments', params: { userId: String(record!.id) } }),
    enabled: open && isEdit && isWarehouseManager && canAssignWarehouse && !!record?.id,
  });
  // Pre-fill the warehouse multi-select with the WM's current assignments when editing.
  useEffect(() => {
    if (open && existingAssignments) form.setFieldValue('warehouseIds', existingAssignments.map((a: any) => String(a.warehouseId)));
  }, [open, existingAssignments, form]);

  // Reports-to is a manual pick (decoupled from territory). Keep any currently-selected value
  // visible even if it isn't in the filtered manager list (e.g. editing a legacy value).
  let reportsOptions = managerOptions;
  if (reportsToVal && !reportsOptions.some((o: any) => o.value === String(reportsToVal))) {
    const found = (allStaff?.data ?? []).find((u: any) => String(u.id) === String(reportsToVal));
    // Fall back to a labelled placeholder rather than letting the Select print the raw id. A manager
    // who is outside the filtered list (or whose directory page hasn't loaded) rendered as "69",
    // which reads like a broken field rather than "someone we can't name right now".
    reportsOptions = [...reportsOptions, found
      ? { value: String(found.id), label: found.fullName }
      : { value: String(reportsToVal), label: `Current manager (#${reportsToVal})` }];
  }

  // The user's PERMANENT bindings — used to prefill the picker on edit (⭐ = every territory owned).
  const { data: myAssignments } = useQuery({
    queryKey: ['user-territories', record?.id],
    queryFn: () => request<any[]>({ url: `/territory-assignments/user/${record!.id}` }),
    enabled: open && isEdit && !!record?.id && recordHasTerr,
  });
  /**
   * Have the existing bindings actually arrived? Until they have, the picker legitimately holds an
   * EMPTY selection — and an empty selection saved to a territory role is indistinguishable from
   * "unbind everything", which is how a save landed a few seconds after opening the dialog silently
   * wiped a Sales Head's district. Nothing may be written from that not-yet-loaded state.
   */
  const terrBindingsReady = !isEdit || !recordHasTerr || myAssignments !== undefined;
  // Seed the SYNC (record / create-default) fields via initialValues (on the <Form> below) + a per-record
  // key, so the form mounts fresh with the right values every open — avoiding the setFieldsValue-in-effect
  // race against the ui-Modal remount (blank fields) and a prior open's roleCodes/mobile leaking in. The
  // ASYNC bindings (territorySel from myAssignments, warehouseIds from existingAssignments) are still
  // filled by their own effects once those queries resolve, and correctly win over these initial values.
  const initialValues = record
    ? {
        fullName: record.fullName,
        email: record.email,
        mobile: record.mobile,
        roleCodes: [record.role?.code ?? record.roleCode].filter(Boolean),
        // territorySel starts empty here and is filled by the effect BELOW once the bindings load.
        territorySel: { ids: [], primaryIds: [] },
        reportsTo: record.reportsTo ? String(record.reportsTo) : undefined,
        languagePref: record.languagePref ?? 'en',
        profilePhotoUrl: record.profilePhotoUrl ?? undefined,
      }
    : { languagePref: 'en', roleCodes: ['SALES_PERSON'], territorySel: { ids: [], primaryIds: [] } };

  // Prefill the territory picker from the user's PERMANENT bindings (edit only). Declared AFTER the
  // record-reset effect ON PURPOSE: React runs effects top-to-bottom, so when a fast reopen fires both
  // on the same commit (the ['user-territories'] query already warm in React Query's cache), THIS one
  // runs last and wins — otherwise the reset above would clobber it to empty and a later save would
  // wipe the SP's whole territory book. ⭐ = every territory the SP owns (is_primary → gold).
  useEffect(() => {
    if (!open || !isEdit || !myAssignments) return;
    const perm = myAssignments.filter((a: any) => a.kind !== 'COVERAGE');
    const tIds = perm.map((a: any) => String(a.territoryId));
    const ownedIds = perm.filter((a: any) => Number(a.isPrimary) === 1).map((a: any) => String(a.territoryId));
    form.setFieldValue('territorySel', { ids: tIds, primaryIds: ownedIds });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isEdit, myAssignments]);

  const save = useMutation({
    mutationFn: async (vals: any) => {
      const roleCodes: string[] = sortBySeniority(vals.roleCodes ?? []); // most-senior first → primary
      const primaryRole = roleCodes[0];
      const isTerrRole = roleCodes.includes('SALES_PERSON') || roleCodes.includes('SALES_HEAD');
      const isWhm = roleCodes.includes('WAREHOUSE_MANAGER');
      const sel: TerritorySelection = vals.territorySel ?? { ids: [], primaryIds: [] };
      const payload: any = {
        fullName: vals.fullName,
        email: vals.email || undefined,
        mobile: vals.mobile,
        roleCode: primaryRole,     // the user's single role
        roleCodes,                 // sent as a 1-element array (back-compat shape; no combinations)
        // Roles in REPORTS_TO have a manager; SUPER_ADMIN (and anything else) is cleared.
        reportsTo: primaryRole && REPORTS_TO[primaryRole] ? (vals.reportsTo ?? null) : null,
        languagePref: vals.languagePref,
        // '' clears it server-side; undefined leaves it alone.
        profilePhotoUrl: vals.profilePhotoUrl ?? '',
      };
      // Territory roles send the multi-select → backend diffs it into the coverage bindings
      // (syncUserTerritories). The ⭐ base becomes the stored home territory. Omitted entirely while
      // the existing bindings are still in flight: the backend leaves them alone when the field is
      // absent, whereas sending the empty picker would delete every one of them.
      if (isTerrRole && terrBindingsReady) {
        payload.territoryIds = sel.ids;
        payload.primaryTerritoryIds = sel.primaryIds;
      }
      // OTP-only platform: no password field needed. Backend creates user without a hash.
      if (!isEdit) payload.password = `Bootstrap_${Math.random().toString(36).slice(2, 10)}`; // throwaway, user never uses
      const result: any = isEdit
        ? await request({ url: `/users/${record.id}`, method: 'PATCH', data: payload })
        : await request({ url: '/users', method: 'POST', data: payload });

      // Sync warehouse assignments for a Warehouse Manager (only when the actor may assign).
      if (isWhm && canAssignWarehouse) {
        const userId = String(isEdit ? record.id : result?.id);
        const wanted = ((vals.warehouseIds ?? []) as any[]).map(String);
        const current = (await request<any[]>({ url: '/warehouse-assignments', params: { userId } })) ?? [];
        const currentWh = new Set(current.map((a: any) => String(a.warehouseId)));
        const hasPrimary = current.some((a: any) => a.isPrimary);
        // Add newly-selected warehouses (the first becomes primary if none exists yet).
        for (let i = 0; i < wanted.length; i++) {
          if (!currentWh.has(wanted[i])) {
            await request({ url: '/warehouse-assignments', method: 'POST', data: { userId, warehouseId: wanted[i], isPrimary: !hasPrimary && i === 0 } });
          }
        }
        // Remove de-selected warehouses.
        for (const a of current) {
          if (!wanted.includes(String(a.warehouseId))) {
            await request({ url: `/warehouse-assignments/${a.id}`, method: 'DELETE' });
          }
        }
      }
      return result;
    },
    onSuccess: () => {
      message.success(isEdit ? 'User updated' : 'User created — they can now OTP-login from their mobile');
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['warehouse-assignments'] });
      qc.invalidateQueries({ queryKey: ['wm-users'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  // Changing a login number (Super-Admin-only) re-points OTP sign-in — confirm before committing.
  const submit = (v: any) => {
    if (isEdit && isSuperAdmin && v.mobile && record?.mobile && v.mobile !== record.mobile) {
      Modal.confirm({
        title: 'Change login number?',
        content: `This re-points ${record.fullName}'s login to ${v.mobile}. They'll receive the OTP on the new number to sign in.`,
        okText: 'Change number',
        okButtonProps: { danger: true },
        onOk: () => save.mutate(v),
      });
      return;
    }
    confirmAction(
      { title: isEdit ? 'Save this user?' : 'Create this user?', content: 'Their role and access take effect as soon as you save.', okText: 'Yes, save' },
      () => save.mutate(v),
    );
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={isEdit ? `Edit · ${record?.fullName}` : 'New user (OTP-only)'}
      width={720}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          {/* Held until the territory bindings land, so nobody can save a form that has not finished
              telling them what this person currently covers. */}
          <Button
            variant="primary"
            loading={save.isPending || !terrBindingsReady}
            disabled={!terrBindingsReady}
            title={!terrBindingsReady ? 'Loading this user’s territories…' : undefined}
            onClick={() => form.submit()}
          >{isEdit ? 'Update' : 'Create'}</Button>
        </>
      }
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="OTP login"
        description="The mobile entered here becomes the user's login. They'll receive OTP via WhatsApp to sign in. No password is set."
      />
      <Form key={`user-${record?.id ?? 'new'}`} form={form} initialValues={initialValues} layout="vertical" onFinish={submit}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="profilePhotoUrl" label="Photo"
              extra="Shown in the staff register and on their profile.">
              <FileUploadField category="profile-photo" buttonLabel="Upload photo" />
            </Form.Item>
            <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="mobile" label="Mobile (WhatsApp)" rules={mobileRules()}
              extra={isEdit && isSuperAdmin ? 'This is the login number — changing it re-points their OTP sign-in.' : undefined}>
              <PhoneInput disabled={isEdit && !isSuperAdmin} placeholder="Mobile number" />
            </Form.Item>
          </Col>
          <Col span={12}>
            {/* One role per user. Stored as a 1-element `roleCodes` array (so every downstream
                derivation keeps working) but presented as a single-select — a Sales Head already
                includes Sales-Person field duties, so no role combinations are offered. */}
            <Form.Item name="roleCodes" label="Role" rules={[{ required: true, message: 'Pick a role' }]}
              extra="Each user has one role. A Sales Head already includes Sales-Person field duties (punch-in, visits, owning a beat)."
              getValueProps={(v) => ({ value: Array.isArray(v) ? v[0] : v })}
              normalize={(v) => (v ? [v] : [])}>
              <Select
                showSearch
                optionFilterProp="label"
                popupMatchSelectWidth={false}
                options={ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] ?? r }))}
                onChange={() => form.setFieldValue('reportsTo', undefined)}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="languagePref" label="Language">
              <Select options={LANGS} />
            </Form.Item>
          </Col>
          {reportsCfg && (
            <Col span={12}>
              <Form.Item name="reportsTo" label={reportsCfg.label}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select a manager"
                  options={reportsOptions}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item name="email" label="Email">
              <Input type="email" placeholder="user@greenbaybio.com" />
            </Form.Item>
          </Col>
          {isTerritoryRole && (
            <Col span={24}>
              <Form.Item name="territorySel" label={pickerIsSp ? 'Territories (home + coverage)' : 'Territories (oversight)'}
                extra={pickerIsSp
                  ? 'Pick the districts/beats this Sales Person works — ★ the one that owns new customers. Kept in sync with Territory Coverage.'
                  : 'Pick the states/districts/beats this Sales Head oversees. Kept in sync with Territory Coverage.'}>
                <TerritoryPicker role={terrRole!} />
              </Form.Item>
            </Col>
          )}
          {isWarehouseManager && canAssignWarehouse && (
            <Col span={12}>
              <Form.Item name="warehouseIds" label="Warehouse(s)"
                extra="The warehouses this manager operates — the first selected becomes their primary.">
                <Select
                  mode="multiple"
                  allowClear
                  showSearch
                  popupMatchSelectWidth={false}
                  optionFilterProp="text"
                  optionLabelProp="text"
                  placeholder="Assign warehouse(s)"
                  options={warehouseOptions}
                />
              </Form.Item>
            </Col>
          )}
          {isWarehouseManager && !canAssignWarehouse && (
            <Col span={24}>
              <Alert type="info" showIcon style={{ marginBottom: 8 }}
                message="Warehouse assignment"
                description="After creating, an admin can bind this manager to their warehouse(s) under More → Warehouse Managers." />
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
}
